import type { ScrollSmoother as Smoother } from "gsap/ScrollSmoother";
import type { SplitText } from "gsap/SplitText";
import {
  gsap,
  ScrollTrigger,
  ScrollSmoother,
  SplitText as SplitTextClass,
  U,
  EASE_IO,
  EASE_OUT,
  EASE_IN,
} from "@/lib/motion/gsap";
import {
  mapRect,
  coverScale,
  centerDelta,
  workBgClip,
  clipTo,
  canPlay,
  canReverse,
} from "@/lib/motion/geom";
import { et, killTrigger } from "@/lib/motion/triggers";
import { resolveDataSrc } from "@/lib/media-src";
import { dirMul, isRtl } from "@/lib/motion/dir";
import {
  createWorkSlider,
  preloadSlides,
  type WorkSlider,
} from "@/lib/motion/work-slider";

export class SiteMotion {
  body!: HTMLElement;
  header!: HTMLElement | null;
  smoother: Smoother | null = null;
  slider: WorkSlider | null = null;
  isTouch = false;
  heroPlayed = false;
  seekMode = false;
  resizing = false;
  lastWidth = 0;
  jumpIndex = 0;
  headerTl: gsap.core.Timeline | null = null;
  jumpListTl: gsap.core.Timeline | null = null;
  heroA: gsap.core.Timeline | null = null;
  heroB: gsap.core.Timeline | null = null;
  servicesTl: gsap.core.Timeline | null = null;
  productsTl: gsap.core.Timeline | null = null;
  storyTl: gsap.core.Timeline | null = null;
  contactTl: gsap.core.Timeline | null = null;
  caseIntro: gsap.core.Timeline | null = null;
  sideTl: gsap.core.Timeline | null = null;
  caseTl: gsap.core.Timeline | null = null;
  projectsSplit: SplitText | null = null;
  caseIndex = 1;
  casePrev = 1;
  sideKey: string | null = null;
  sidePrev: string | null = null;
  loaderPlayed = false;
  private chromeBooted = false;
  servicesArmed = true;
  serviceIndex = 0;
  lastServiceKey = "side-service-1";
  lastProductKey = "side-product-1";
  thumbGap = 0;
  caseIntroTimer: ReturnType<typeof setTimeout> | undefined;
  appResizeTimer: ReturnType<typeof setTimeout> | undefined;
  vhTimer: ReturnType<typeof setTimeout> | undefined;
  lockFailsafe: ReturnType<typeof setTimeout> | undefined;
  spine: {
    reset(): void;
    rebuildSplits(): void;
    rebuild(): void;
    logoLoops(): void;
    swapCase(from: number, to: number): void;
    hideProjectsCopy(): void;
    settleJump(section: string): void;
    bindOnce(): void;
    placeThumbs(): void;
    buildHeroA(): void;
    buildHeroB(): void;
    buildCases(): void;
    buildServices(): void;
    buildProducts(): void;
    buildStory(): void;
    buildContact(): void;
    buildProgress(): void;
  } | null = null;
  requestRebuild: () => void = () => {};
  /** Promise that resolves after reset + epoch slice rebuild + ST.refresh. */
  requestRebuildAsync: () => Promise<void> = () => Promise.resolve();
  /** True while a section enter/exit timeline owns the page (wheel is swallowed). */
  scrollLocked = false;
  unsub: Array<() => void> = [];

  lock(locked: boolean, opts?: { force?: boolean }) {
    if ((this.seekMode || this.resizing) && !opts?.force) return;
    // Do not toggle body overflow — that hid the scrollbar and shifted layout.
    // Overflow is forced to scroll via globals.css. Pause smoother + swallow
    // wheel while a section timeline plays so flicks cannot skip `et()` gates.
    this.scrollLocked = locked;
    this.smoother?.paused(locked);
    if (this.lockFailsafe) {
      clearTimeout(this.lockFailsafe);
      this.lockFailsafe = undefined;
    }
    if (locked) {
      // Failsafe: never leave scroll frozen if an unlock path is skipped.
      // It must not cut a transition short either — the Products reveal runs
      // 2.5s, so a flat 2200ms timer released scroll mid-play and the trailing
      // flick drove `et()` gates while the plane was still handing over.
      // Re-arm while a section timeline is genuinely still running, capped so
      // a stuck timeline can never freeze the page for good.
      // Also re-arm while the boot loader still covers (`body.progress`) so the
      // failsafe cannot unlock under the splash and let the page scroll.
      let extensions = 0;
      const arm = () => {
        this.lockFailsafe = setTimeout(() => {
          this.lockFailsafe = undefined;
          const bootSplash =
            this.body?.classList.contains("progress") &&
            !this.body?.classList.contains("site-ready");
          if (
            extensions < 8 &&
            (this.sectionTlActive() || bootSplash || this.seekMode)
          ) {
            extensions += 1;
            arm();
            return;
          }
          this.scrollLocked = false;
          this.smoother?.paused(false);
        }, 2200);
      };
      arm();
    }
  }

  /** A section enter/exit timeline currently owns the page. */
  sectionTlActive() {
    return (
      !!this.heroA?.isActive() ||
      !!this.heroB?.isActive() ||
      !!this.servicesTl?.isActive() ||
      !!this.productsTl?.isActive() ||
      !!this.storyTl?.isActive() ||
      !!this.contactTl?.isActive()
    );
  }

  /**
   * `--vh` sizes scroll runways (stable on touch — width-only updates so
   * toolbar show/hide does not move every ScrollTrigger gate).
   * `--vh2` sizes fixed visual layers and always tracks the live viewport.
   */
  setVh() {
    const apply = () => {
      this.body.classList.add("progress");
      document.documentElement.style.setProperty("--vh", `${this.vh}px`);
      if (this.vhTimer) clearTimeout(this.vhTimer);
      this.vhTimer = setTimeout(() => this.body.classList.remove("progress"), 250);
    };
    this.vh = 0.01 * window.innerHeight;
    this.setVh2();
    if (this.isTouch) {
      if (window.innerWidth !== this.lastWidth) apply();
    } else apply();
    this.lastWidth = window.innerWidth;
  }

  /** Live visual viewport height only — safe to call on every toolbar change. */
  setVh2() {
    const vv = window.visualViewport;
    const h = vv?.height ?? window.innerHeight;
    this.vh2 = 0.01 * h;
    document.documentElement.style.setProperty("--vh2", `${this.vh2}px`);
  }

  vh = 0;
  vh2 = 0;

  /** Current scroll-throttle gesture id (one flick = one section advance). */
  gestureId = 0;
  /** True while wheel/touch input is mid-gesture (between first tick and idle/end). */
  gestureLive = false;
  /** Gesture that last crossed a section gate — throttle swallows until it changes. */
  lastAdvanceGesture = -1;
  /**
   * Bound by `bindScrollThrottle` — stop an in-flight touch fling (e.g. when a
   * Work case gate fires so one flick cannot skip MKIT→Kenaz→CareLend→more).
   */
  consumeTouchAdvance: () => void = () => {};

  setJumpIndex(n: number) {
    this.jumpListTl?.kill();
    this.jumpListTl = gsap.timeline();
    this.jumpListTl.to(
      ".jump_menu_list",
      { y: `${-100 * n}%`, duration: 1, ease: EASE_OUT },
      0,
    );
    this.jumpIndex = n;
  }

  playFkVideos(mode: "play" | "pause") {
    document.querySelectorAll<HTMLVideoElement>(".fk_thumb video").forEach((v) => {
      void this.controlMedia(v, mode);
    });
  }

  /** Latest requested play/pause per element — stale play() must not override pause. */
  private mediaIntent = new WeakMap<HTMLMediaElement, "play" | "pause">();

  async controlMedia(el: HTMLMediaElement, mode: "play" | "pause") {
    this.mediaIntent.set(el, mode);
    if (mode === "pause") {
      el.pause();
      return;
    }
    try {
      await el.play();
    } catch (err) {
      /* Interrupted play()/pause() races — expected, never surface AbortError. */
      if (err instanceof DOMException && err.name === "AbortError") return;
    }
    if (this.mediaIntent.get(el) === "pause") el.pause();
  }

  /** Load product video sources once Services (or later) owns the spine. */
  preloadProductVideos(timeoutMs = 1200): Promise<void> {
    const videos = [
      ...document.querySelectorAll<HTMLVideoElement>("#products video"),
    ];
    const waiters = videos.map((el) => {
      const deferred = el.dataset.src;
      if (deferred && !el.getAttribute("src")) {
        el.src = deferred;
        el.load();
      }
      if (el.readyState >= 2) return Promise.resolve();
      return new Promise<void>((resolve) => {
        const done = () => {
          el.removeEventListener("loadeddata", done);
          el.removeEventListener("error", done);
          resolve();
        };
        el.addEventListener("loadeddata", done, { once: true });
        el.addEventListener("error", done, { once: true });
        window.setTimeout(done, timeoutMs);
      });
    });
    return Promise.all(waiters).then(() => undefined);
  }

  playProductVideos(mode: "play" | "pause") {
    document.querySelectorAll("#products video").forEach((v) => {
      const el = v as HTMLVideoElement;
      const deferred = el.dataset.src;
      if (mode === "play" && deferred && !el.getAttribute("src")) {
        el.src = deferred;
        el.load();
      }
      void this.controlMedia(el, mode);
    });
  }

  syncSize(el: HTMLElement, source: Element) {
    const apply = (r: DOMRectReadOnly | DOMRect) => {
      el.style.width = `${r.width}px`;
      el.style.height = `${r.height}px`;
    };
    apply(source.getBoundingClientRect());
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) apply(e.contentRect);
    });
    ro.observe(source);
    this.unsub.push(() => ro.disconnect());
  }

  bootChrome() {
    if (this.chromeBooted) return;
    this.chromeBooted = true;
    this.hydrateMedia();
    this.bindMagnets();
    this.bindLabels();
    this.bindOdometers();
    this.bindScrollTo();
    this.bindSticky();
    this.bindForm();
    this.bindMarquees();
    this.refreshInviewCache();
    ScrollTrigger.create({ onUpdate: () => this.inview(), start: 0, end: "max" });
  }

  hydrateMedia() {
    document.querySelectorAll<HTMLElement>(".load_video").forEach((wrap) => {
      const src = wrap.dataset.src;
      if (!src || wrap.querySelector("video")) return;
      const n = document.createElement("video");
      n.muted = true;
      n.setAttribute("muted", "");
      n.playsInline = true;
      n.setAttribute("playsinline", "");
      n.setAttribute("webkit-playsinline", "");
      n.autoplay = true;
      n.setAttribute("autoplay", "");
      n.loop = true;
      n.controls = false;
      n.preload = "auto";
      n.src = src;
      wrap.appendChild(n);
      n.load();
      const play = () => {
        void this.controlMedia(n, "play");
      };
      n.addEventListener("loadedmetadata", play, { once: true });
      play();
      const unlock = () => {
        void this.controlMedia(n, "play");
        window.removeEventListener("touchstart", unlock, true);
        window.removeEventListener("click", unlock, true);
      };
      window.addEventListener("touchstart", unlock, true);
      window.addEventListener("click", unlock, true);
    });
    const imgs = document.querySelectorAll<HTMLImageElement>(".load_img");
    imgs.forEach((img, i) => {
      const src = img.dataset.src;
      if (!src) return;
      img.setAttribute("src", src);
      img.classList.remove("load_img");
      const probe = new Image();
      probe.onload = () => {
        if (i === imgs.length - 1) ScrollTrigger.refresh();
      };
      probe.src = src;
    });
    document.querySelectorAll<HTMLElement>(".load_bg").forEach((el) => {
      const src = resolveDataSrc(el);
      if (!src) return;
      el.style.backgroundImage = `url(${src})`;
      el.classList.remove("load_img");
    });
  }

  bindMagnets() {
    if (this.isTouch) return;
    let frame = 0;
    let last: MouseEvent | null = null;
    const apply = (e: MouseEvent) => {
      document.querySelectorAll<HTMLElement>(".magnet").forEach((el) => {
        const radius = Number(el.dataset.dist) * 20 || 120;
        const box = el.getBoundingClientRect();
        const cx = box.left + box.width / 2;
        const cy = box.top + box.height / 2;
        const x = e.clientX;
        const y = e.clientY;
        let dx = -0.35 * (cx - x);
        let dy = -0.35 * (cy - y);
        if (radius === 0) {
          dx = 0;
          dy = 0;
        }
        const dist = Math.hypot(x - cx, y - cy);
        if (dist < radius && el.classList.contains("magnet")) {
          gsap.to(el, { y: dy, x: dx, duration: 0.35, ease: "power3.out", overwrite: "auto" });
          el.classList.add("mg");
        } else {
          gsap.to(el, { y: 0, x: 0, duration: 0.35, ease: "power3.out", overwrite: "auto" });
          el.classList.remove("mg");
        }
      });
    };
    const onMove = (e: MouseEvent) => {
      last = e;
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        if (last) apply(last);
      });
    };
    document.addEventListener("mousemove", onMove);
    this.unsub.push(() => {
      document.removeEventListener("mousemove", onMove);
      if (frame) cancelAnimationFrame(frame);
    });
  }

  bindLabels() {
    if (this.isTouch) return;
      const lang = document.documentElement.getAttribute("lang");
      const arabic = isRtl();
      document.querySelectorAll<HTMLElement>(".interactive_label").forEach((el) => {
      const txt = el.querySelector("._txt");
      const shape = el.querySelector("._shape");
      const tl = gsap.timeline({ paused: true });
      let targets: Element[] = [];
      let stagger = 0.1;
      if (txt) {
        if (arabic) {
          targets = [txt];
          stagger = 0;
        } else {
          const type = txt.classList.contains("words")
            ? "words"
            : lang === "en"
              ? "chars"
              : "words";
          stagger = type === "chars" ? 0.01 : 0.1;
          const split = new SplitTextClass(txt, {
            type,
            wordsClass: "SplitClass",
            charsClass: "SplitClass",
          });
          targets = type === "chars" ? split.chars : split.words;
        }
      }
      const all = [...targets, ...(shape ? [shape] : [])];
      if (!all.length) return;
      tl.to(all, { y: "-50%", autoAlpha: 0, duration: 0.25, ease: EASE_IN, stagger }, 0)
        .set(all, { y: "50%" })
        .to(all, { y: "0%", autoAlpha: 1, duration: 0.25, ease: EASE_OUT, stagger });
      const enter = () => {
        if (!tl.isActive()) tl.restart();
      };
      el.addEventListener("mouseenter", enter);
      this.unsub.push(() => el.removeEventListener("mouseenter", enter));
    });
  }

  bindOdometers() {
    document.querySelectorAll<HTMLElement>(".odometer").forEach((el) => {
      if (el.classList.contains("paused")) return;
      ScrollTrigger.create({
        trigger: el,
        start: "0% 100%",
        invalidateOnRefresh: true,
        scrub: 1,
        onEnter: () => {
          if (el.dataset.counter) el.innerHTML = el.dataset.counter;
        },
      });
    });
  }

  bindScrollTo() {
    document.querySelectorAll<HTMLElement>(".scrollTo").forEach((el) => {
      const onClick = (e: Event) => {
        /* Ghost side-nav items (opacity 0 / pe none) must not scroll. */
        const nav = el.closest<HTMLElement>(".side_nav");
        if (nav) {
          if (!nav.classList.contains("is-active")) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }
          const cs = getComputedStyle(nav);
          if (
            cs.visibility === "hidden" ||
            cs.pointerEvents === "none" ||
            Number.parseFloat(cs.opacity || "0") < 0.05
          ) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }
        }
        const id = el.dataset.scroll;
        const margin = Number(el.dataset.margin || 0);
        const target = id ? document.getElementById(id) : null;
        const top =
          id === "end"
            ? document.body.scrollHeight - window.innerHeight
            : target
              ? target.getBoundingClientRect().top + window.pageYOffset
              : 0;
        if (el.dataset.direct) {
          gsap.set(this.smoother, { scrollTop: top + 2 });
        } else {
          gsap.to(this.smoother, {
            scrollTop: top + 2 + (target ? target.offsetHeight * margin : 0),
            duration: 1,
            ease: EASE_OUT,
          });
        }
      };
      el.addEventListener("click", onClick);
      this.unsub.push(() => el.removeEventListener("click", onClick));
    });
  }

  bindSticky() {
    const wraps = document.querySelectorAll("._sticky_wrap");
    ScrollTrigger.matchMedia({
      "(min-width: 601px)": () => {
        wraps.forEach((wrap, i) => {
          const pin = wrap.querySelector("._sticky");
          if (!pin) return;
          ScrollTrigger.create({
            id: `t-${i}`,
            invalidateOnRefresh: true,
            trigger: pin,
            start: "0% 0%",
            end: () =>
              `+=${(wrap as HTMLElement).offsetHeight - (pin as HTMLElement).offsetHeight}`,
            pin: true,
            pinSpacing: false,
          });
        });
      },
      "(max-width: 600px)": () => {
        wraps.forEach((_, i) => killTrigger(`t-${i}`));
      },
    });
  }

  bindForm() {
    document
      .querySelectorAll<HTMLFormElement>("._form:not([data-client-validate])")
      .forEach((form) => {
      const btn = form.querySelector(".button");
      const submit = form.querySelector<HTMLInputElement>("._submit");
      btn?.addEventListener("click", () => submit?.click());
      form.addEventListener("submit", (e) => {
        e.preventDefault();
        const data = Object.fromEntries(new FormData(form).entries());
        fetch("/api/send-email", {
          method: "POST",
          body: JSON.stringify(data),
          headers: { "Content-Type": "application/json", Accept: "application/json" },
        })
          .then((r) => r.json())
          .then((r) => {
            if (r.status === "success") {
              gsap
                .timeline()
                .to(".form_wrap", { autoAlpha: 0, duration: 0.5, ease: EASE_IN })
                .to(".form_success", { autoAlpha: 1, duration: 0.5, ease: EASE_OUT });
            } else {
              alert("Error: " + (r.message || "Something went wrong. Please try again."));
            }
          })
          .catch(() => {
            gsap
              .timeline()
              .to(".form_wrap", { autoAlpha: 0, duration: 0.5, ease: EASE_IN })
              .to(".form_success", { autoAlpha: 1, duration: 0.5, ease: EASE_OUT });
          });
      });
    });
  }

  bindMarquees() {
    document.querySelectorAll<HTMLElement>(".curWrap").forEach((wrap) => {
      const items = Array.from(wrap.querySelectorAll<HTMLElement>(".cur_col"));
      if (!items.length) return;
      wrap.style.overflow = "hidden";
      if (getComputedStyle(wrap).position === "static") wrap.style.position = "relative";
      let total = 0;
      const widths: number[] = [];
      items.forEach((item) => {
        const w = item.getBoundingClientRect().width;
        /* Physical left rail — dirMul only flips scroll direction. */
        gsap.set(item, { position: "absolute", left: total, willChange: "transform" });
        total += w;
        widths.push(total);
      });
      const dur = Number(wrap.dataset.dur) || 18;
      const dir = (wrap.dataset.dir === "ltr" ? -1 : 1) * dirMul();
      const pxPerSec = total / Math.max(0.001, dur);
      let pos = 0;
      const tick = (_time: number, delta: number) => {
        const dt = delta / 1000;
        pos += dir * pxPerSec * dt;
        const span = total;
        pos = ((((pos + span) % span) + span) % span) - span;
        items.forEach((item, i) => {
          const cum = widths[i]!;
          let x = pos + (i === 0 ? 0 : widths[i - 1]!);
          x = ((x % span) + span) % span;
          if (x > span - cum) x -= span;
          gsap.set(item, { x });
        });
      };
      gsap.ticker.add(tick);
      this.unsub.push(() => gsap.ticker.remove(tick));
    });
  }

  private inviewCache: {
    wraps: NodeListOf<Element> | null;
    splits: NodeListOf<Element> | null;
  } = { wraps: null, splits: null };

  refreshInviewCache() {
    this.inviewCache.wraps = document.querySelectorAll("._eleWrap");
    this.inviewCache.splits = document.querySelectorAll("._splitWrap");
  }

  inview() {
    if (!this.inviewCache.wraps || !this.inviewCache.splits) {
      this.refreshInviewCache();
    }
    this.inviewCache.wraps?.forEach((wrap) => {
      if (!ScrollTrigger.isInViewport(wrap) || wrap.classList.contains("inview")) return;
      wrap.classList.add("inview");
      gsap.set(wrap, { autoAlpha: 1 });
      const ys = wrap.querySelectorAll("._eleY");
      const xs = wrap.querySelectorAll("._eleX");
      if (ys.length) {
        gsap.set(ys, { y: 0.1 * window.innerHeight, autoAlpha: 0 });
        gsap.to(ys, {
          y: 0,
          autoAlpha: 1,
          duration: 1,
          ease: EASE_OUT,
          delay: 0.25,
          stagger: 0.2,
        });
      }
      if (xs.length) {
        gsap.set(xs, { x: dirMul() * 0.01 * window.innerWidth, autoAlpha: 0 });
        gsap.to(xs, {
          x: 0,
          autoAlpha: 1,
          duration: 1,
          ease: EASE_OUT,
          delay: 0.25,
          stagger: 0.1,
        });
      }
    });
    this.inviewCache.splits?.forEach((wrap) => {
      if (!ScrollTrigger.isInViewport(wrap)) return;
      wrap.querySelectorAll("._splitWords").forEach((el) =>
        this.splitIn(el, isRtl() ? "lines" : "words"),
      );
      wrap.querySelectorAll("._splitLines").forEach((el) => this.splitIn(el, "lines"));
    });
    if (this.isTouch && !this.heroPlayed && this.headerTl) this.headerTl.play();
  }

  splitIn(el: Element, kind: "words" | "lines") {
    if (el.classList.contains("inview")) return;
    el.classList.add("inview");
    gsap.set(el, { autoAlpha: 1 });
    if (kind === "words") {
      const split = new SplitTextClass(el, { type: "words", wordsClass: "SplitClass" });
      const dirX = el.classList.contains("dirX");
      gsap.set(split.words, dirX ? { x: 30 * dirMul(), autoAlpha: 0 } : { y: "100%", autoAlpha: 0 });
      gsap.to(split.words, {
        x: 0,
        y: 0,
        autoAlpha: 1,
        duration: 1,
        ease: EASE_OUT,
        stagger: 0.1,
        delay: 0.25,
      });
    } else {
      const split = new SplitTextClass(el, { type: "lines", linesClass: "SplitClass" });
      gsap.set(split.lines, { y: "100%" });
      gsap.to(split.lines, {
        y: 0,
        autoAlpha: 1,
        duration: 1,
        ease: EASE_OUT,
        stagger: 0.2,
        delay: 0.25,
      });
    }
  }

  destroy() {
    this.unsub.forEach((fn) => fn());
    this.unsub = [];
    this.projectsSplit?.revert();
    this.projectsSplit = null;
    document.querySelectorAll<HTMLElement>("._split").forEach((el) => {
      (el as HTMLElement & { _splitInstance?: { revert(): void } })._splitInstance?.revert();
      delete (el as HTMLElement & { _splitInstance?: unknown })._splitInstance;
    });
    this.slider?.destroy();
    this.smoother?.kill();
    ScrollTrigger.getAll().forEach((t) => t.kill());
    this.chromeBooted = false;
  }
}

export type { SiteMotion as SiteMotionType };
